/**
 * Counterbalance surfaces — page-level banner and per-row block.
 *
 * Two places, on purpose:
 *   • CounterbalanceBanner — at the top of the board, so a collapsed
 *     arrangement is visible without opening a row. The 2026-07-27 drift went
 *     unnoticed for a week precisely because nothing said anything anywhere.
 *   • CounterbalanceBlock — inside the expanded row, on HEALTHY rows too. An
 *     operator should meet the arrangement before they break it, not after.
 *
 * Neither blocks anything. The copy says what is at stake in one sentence and
 * leaves the decision where it belongs.
 */

import React from 'react';
import { Box, Typography, Alert, AlertTitle, Chip, Tooltip } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { colors, typography } from '../../../theme/designTokens';
import { PipelineModel, PipelineCounterbalanceMembership } from '../../../services/quillAdminService';
import {
  CounterbalanceConflict,
  conflictHeadline,
  deriveCounterbalanceConflicts,
  distinctNoun,
  partnerSummary,
} from './counterbalance';

const roleLabel = (role: string | null): string => role ?? 'role';

/** The page-level warning. Renders nothing when every chain is intact. */
export const CounterbalanceBanner: React.FC<{ pipelines: PipelineModel[] }> = ({ pipelines }) => {
  const conflicts = React.useMemo(() => deriveCounterbalanceConflicts(pipelines), [pipelines]);
  if (conflicts.length === 0) return null;

  return (
    <Alert
      severity="warning"
      icon={<CompareArrowsIcon />}
      sx={{ mb: 2, borderRadius: '16px' }}
      data-testid="counterbalance-banner"
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        {conflicts.length === 1
          ? 'One pair of pipelines has lost its independent check'
          : `${conflicts.length} pairs of pipelines have lost their independent check`}
      </AlertTitle>
      {conflicts.map((conflict) => (
        <Box key={conflict.id} sx={{ mb: 1.25 }} data-testid={`counterbalance-conflict-${conflict.id}`}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {conflictHeadline(conflict)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {conflict.why}
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontFamily: typography.fonts.mono, color: 'text.disabled', mt: 0.25 }}
          >
            {conflict.label} — {conflict.a.pipeline_key} ({roleLabel(conflict.a.role)}) ·{' '}
            {conflict.b.pipeline_key} ({roleLabel(conflict.b.role)})
          </Typography>
        </Box>
      ))}
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Nothing is blocked. If this was deliberate, leave it — this is here so it is never
        a surprise.
      </Typography>
    </Alert>
  );
};

/** One chain membership, rendered inside the expanded row. */
const ChainBlock: React.FC<{ membership: PipelineCounterbalanceMembership }> = ({ membership }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: '12px',
      backgroundColor: membership.collapsed ? colors.warning[50] : colors.gray[50],
      border: `1px solid ${membership.collapsed ? colors.warning.light : colors.gray[200]}`,
    }}
    data-testid={`counterbalance-chain-${membership.chain_key}`}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
      <CompareArrowsIcon
        sx={{ fontSize: 18, color: membership.collapsed ? colors.warning.dark : colors.gray[600] }}
      />
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {membership.label}
      </Typography>
      <Chip label={roleLabel(membership.role)} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
      {membership.collapsed && (
        <Chip
          label="check lost"
          size="small"
          color="warning"
          sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
        />
      )}
    </Box>

    {membership.partners.map((partner) => (
      <Box key={`${partner.pipeline_key}-${partner.distinct}`} sx={{ mb: 0.75 }}>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 600 }}>
            {partner.display_name}
          </Box>{' '}
          <Box component="span" sx={{ color: 'text.secondary' }}>
            ({roleLabel(partner.role)}) — {partnerSummary(partner)}
          </Box>
        </Typography>
        <Typography
          variant="caption"
          sx={{ display: 'block', color: partner.collapsed ? colors.warning.dark : 'text.secondary' }}
        >
          {partner.collapsed
            ? partner.why
            : `They must not share a ${distinctNoun(partner.distinct)}: ${partner.why}`}
        </Typography>
      </Box>
    ))}

    <Tooltip title={membership.source} arrow>
      <Typography
        variant="caption"
        sx={{ display: 'block', color: 'text.disabled', cursor: 'help', mt: 0.5 }}
      >
        Wired in the engine — hover for the file that establishes it.
      </Typography>
    </Tooltip>
  </Box>
);

/**
 * Every chain this pipeline is a role in. Renders nothing for the pipelines
 * that are in none — most of the registry.
 */
export const CounterbalanceBlock: React.FC<{ pipeline: PipelineModel }> = ({ pipeline }) => {
  const memberships = pipeline.counterbalance ?? [];
  if (memberships.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
      {memberships.map((membership) => (
        <ChainBlock key={membership.chain_key} membership={membership} />
      ))}
    </Box>
  );
};

/** The collapsed-row chip, so a lost check is visible without expanding. */
export const CounterbalanceChip: React.FC<{ conflicts: CounterbalanceConflict[] }> = ({ conflicts }) => {
  if (conflicts.length === 0) return null;
  const title = conflicts
    .map((c) => `${conflictHeadline(c)} ${c.why}`)
    .join(' ');
  return (
    <Tooltip title={title} arrow>
      <Chip
        icon={<CompareArrowsIcon sx={{ fontSize: 14 }} />}
        label="check lost"
        size="small"
        color="warning"
        variant="outlined"
        sx={{ cursor: 'help', flexShrink: 0 }}
      />
    </Tooltip>
  );
};

/** Exported for the summary row, which has only the pipeline in hand. */
export const conflictsForPipeline = (pipeline: PipelineModel): CounterbalanceConflict[] =>
  deriveCounterbalanceConflicts([pipeline]);
